import { ChevronDownIcon } from '@radix-ui/react-icons';
import { useState, useEffect, type FC } from 'react';

import { ArrowLeftRight } from '@/components/icons';
import { cn } from '@/utils/cn';

interface UnitSelectProps {
  defaultValue?: string;
  units: readonly string[];
  onSelect?: (value: string) => void;
  disabled?: boolean;
  className?: string;
  fitContent?: boolean;
  icon?: React.ReactNode;
  variant?: 'selector' | 'button';
}

export const UnitSelect: FC<UnitSelectProps> = ({
  defaultValue,
  units,
  icon,
  onSelect,
  disabled = false,
  className,
  fitContent = false,
  variant = 'button',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(defaultValue || units[0]);
  const [selectedIndex, setSelectedIndex] = useState<number>(
    defaultValue ? units.findIndex((u) => u === defaultValue) : 0,
  );

  const handleSelect = (value: string) => {
    setSelected(value);
    onSelect?.(value);
    setIsOpen(false);
  };

  useEffect(() => {
    if (!units.includes(selected)) {
      handleSelect(defaultValue ?? units[1]); // TEMP fix to base size unit not changing when symbol changes
      setSelectedIndex(
        defaultValue ? units.findIndex((u) => u === defaultValue) : 0,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units, defaultValue, selected]);

  return (
    <div className={cn('relative peer-disabled:opacity-60', className)}>
      <button
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 bg-vestgrey-800 p-2 text-center font-mono transition-colors',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          onSelect ? 'hover:bg-border' : 'cursor-default',
        )}
        onClick={(e) => {
          e.preventDefault();

          if (disabled || units.length === 0) {
            return;
          }

          if (variant === 'button') {
            if (units.length === 1) {
              handleSelect(units[0]);
              return;
            }

            const nextIndex =
              selectedIndex + 1 >= units.length ? 0 : selectedIndex + 1;

            setSelectedIndex(nextIndex);
            handleSelect(units[nextIndex]);
          } else {
            if (units.length > 1) {
              setIsOpen((prev) => !prev);
            }
          }
        }}
        disabled={disabled}
      >
        <div className="relative flex items-center justify-end">
          {fitContent && units.length > 0 && (
            <p className="opacity-0">
              {units.reduce(
                (prev, curr) => (curr.length > prev.length ? curr : prev),
                units[0],
              )}
            </p>
          )}
          <span
            className={cn(
              'text-vestgrey-50',
              fitContent ? 'absolute' : 'min-w-max',
            )}
          >
            {selected}
          </span>
        </div>
        {units.length > 1 &&
          (icon ||
            (variant === 'selector' ? (
              <ChevronDownIcon
                className={cn(
                  'text-vestgrey-700 transition-transform duration-300',
                  {
                    'rotate-180': isOpen,
                  },
                )}
              />
            ) : (
              <ArrowLeftRight size={10} className="text-vestgrey-700" />
            )))}
      </button>
      {variant === 'selector' && isOpen && units.length > 1 && (
        <div className="absolute left-auto right-0 top-full z-30 w-min overflow-hidden border border-border bg-background shadow-lg">
          {units.map((unit) => (
            <button
              type="button"
              className="w-full min-w-max cursor-pointer border-b border-border px-3 py-1.5 font-mono uppercase text-foreground last:border-b-0 hover:bg-vestgrey-800"
              onClick={() => handleSelect(unit)}
              key={unit}
            >
              {unit}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
